'use client';

import SvgChevronRight from '@hc-frontend/core-icons/kit/chevron-right';
import type { ButtonClickEvent } from '@hc-frontend/core-ui';
import { useEventBus, useTrackingContexts } from '@hc-frontend/core-ui';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import type { ChangeEventHandler } from 'react';
import { useCallback, useEffect, useState } from 'react';

import type { FilterSelectProps } from './filter-select.types';

export function FilterSelect({ group }: FilterSelectProps) {
  const eventBus = useEventBus();
  const contexts = useTrackingContexts();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const initialValue = searchParams.get(group.key) ?? '';
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const buildQuery = useCallback(
    (v: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set(group.key, v);
      return params.toString();
    },
    [searchParams, group.key],
  );

  const handleChange: ChangeEventHandler<HTMLSelectElement> = (e) => {
    eventBus.publish<ButtonClickEvent>({
      id: 'filter-select',
      topic: 'core.ui.button.click',
      source: e.currentTarget,
      contexts,
    });

    const query = buildQuery(e.target.value);
    router.replace(`${pathname}?${query}`);
  };

  return (
    <div className="relative">
      <select
        className="hc-btn--secondary-card-dark w-full cursor-pointer appearance-none bg-gray-0 pr-48-48"
        aria-label={group.title}
        onChange={handleChange}
        value={value}
      >
        <option value="" disabled>
          {group.title}
        </option>
        {group.items.map((filter) => (
          <option key={filter.value} value={filter.value}>
            {filter.label ?? filter.value}
          </option>
        ))}
      </select>
      <SvgChevronRight className="pointer-events-none absolute right-16-16 top-1/2 -translate-y-1/2 rotate-90 text-tertiary-900" />
    </div>
  );
}
